// src/pages/emailMarketing/CampaignProgressPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import emailMarketingService from '../../helpers/emailMarketingService';
import Swal from 'sweetalert2';
import { FaSync, FaCheckCircle, FaExclamationTriangle, FaClock } from 'react-icons/fa';

function CampaignProgressPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [autoRefresh, setAutoRefresh] = useState(true);

    useEffect(() => {
        fetchStats();
        
        // Polling cada 5 segundos si autoRefresh está activo y está sending
        let interval;
        if (autoRefresh && stats?.status === 'sending') {
            interval = setInterval(fetchStats, 5000);
        }
        
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [id, autoRefresh, stats?.status]);

    const fetchStats = async () => {
        try {
            const statsData = await emailMarketingService.getCampaignStats(id);
            setStats(statsData);
            
            // Si está completa, redirigir a stats
            if (statsData.pendingCount === 0 && statsData.status !== 'sending') {
                navigate(`/email-marketing/campaigns/${id}/stats`);
            }
        } catch (error) {
            // Error silencioso
        } finally {
            setLoading(false);
        }
    };

    if (loading && !stats) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="text-center">Cargando...</div>
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="text-center">No se encontró la campaña</div>
            </div>
        );
    }

    const progress = stats.totalRecipients > 0 
        ? (stats.sentCount / stats.totalRecipients) * 100 
        : 0;

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold">Progreso de Envío</h1>
                <button
                    onClick={() => navigate(`/email-marketing/campaigns/${id}/stats`)}
                    className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                >
                    Ver Estadísticas Completas
                </button>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">{stats.campaignName}</h2>
                    {stats.status === 'sending' && (
                        <button
                            onClick={() => setAutoRefresh(!autoRefresh)}
                            className={`px-3 py-1 rounded text-sm flex items-center ${
                                autoRefresh ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                            }`}
                        >
                            <FaSync className={`mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
                            {autoRefresh ? 'Auto-actualizando' : 'Pausado'}
                        </button>
                    )}
                </div>
                
                {/* Barra de progreso mejorada */}
                <div className="mb-6">
                    <div className="flex justify-between mb-2">
                        <span className="text-sm font-medium">Progreso de Envío</span>
                        <span className="text-sm font-medium">
                            {stats.sentCount} / {stats.totalRecipients} ({progress.toFixed(1)}%)
                        </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-6 relative overflow-hidden">
                        <div
                            className="bg-gradient-to-r from-blue-500 to-blue-600 h-6 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                            style={{ width: `${progress}%` }}
                        >
                            {progress > 10 && (
                                <span className="text-white text-xs font-medium">{progress.toFixed(0)}%</span>
                            )}
                        </div>
                        {progress < 10 && (
                            <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-gray-700">
                                {progress.toFixed(1)}%
                            </span>
                        )}
                    </div>
                    {stats.status === 'sending' && (
                        <p className="text-xs text-gray-500 mt-2">
                            Procesando aproximadamente 60 emails por minuto
                        </p>
                    )}
                </div>

                {/* Estado actual con iconos */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            {stats.status === 'sending' && <FaSync className="text-yellow-500 mr-2 animate-spin" />}
                            {stats.status === 'sent' && <FaCheckCircle className="text-green-500 mr-2" />}
                            {stats.status === 'failed' && <FaExclamationTriangle className="text-red-500 mr-2" />}
                            {stats.status === 'paused' && <FaClock className="text-orange-500 mr-2" />}
                            <div>
                                <p className="text-sm text-gray-600">Estado</p>
                                <span className={`px-3 py-1 rounded text-white text-sm font-medium ${
                                    stats.status === 'sending' ? 'bg-yellow-500' :
                                    stats.status === 'sent' ? 'bg-green-500' :
                                    stats.status === 'failed' ? 'bg-red-500' :
                                    stats.status === 'paused' ? 'bg-orange-500' :
                                    'bg-gray-500'
                                }`}>
                                    {stats.status === 'sending' ? 'Enviando...' :
                                     stats.status === 'sent' ? 'Completada' :
                                     stats.status === 'failed' ? 'Fallida' :
                                     stats.status === 'paused' ? 'Pausada' :
                                     stats.status}
                                </span>
                            </div>
                        </div>
                        {stats.status === 'sending' && stats.pendingCount > 0 && (
                            <div className="text-right">
                                <p className="text-sm text-gray-600">Tiempo estimado</p>
                                <p className="text-lg font-bold text-blue-600">
                                    ~{Math.ceil(stats.pendingCount / 60)} min
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Métricas detalladas */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="p-4 bg-green-50 rounded-lg">
                        <p className="text-gray-600 text-xs mb-1">Enviados</p>
                        <p className="text-2xl font-bold text-green-600">{stats.sentCount}</p>
                        <p className="text-xs text-gray-500 mt-1">
                            {stats.totalRecipients > 0 ? ((stats.sentCount / stats.totalRecipients) * 100).toFixed(1) : 0}%
                        </p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg">
                        <p className="text-gray-600 text-xs mb-1">Fallidos</p>
                        <p className="text-2xl font-bold text-red-600">{stats.failedCount}</p>
                        <p className="text-xs text-gray-500 mt-1">
                            {stats.totalRecipients > 0 ? ((stats.failedCount / stats.totalRecipients) * 100).toFixed(1) : 0}%
                        </p>
                    </div>
                    <div className="p-4 bg-yellow-50 rounded-lg">
                        <p className="text-gray-600 text-xs mb-1">Pendientes</p>
                        <p className="text-2xl font-bold text-yellow-600">{stats.pendingCount}</p>
                        <p className="text-xs text-gray-500 mt-1">
                            {stats.totalRecipients > 0 ? ((stats.pendingCount / stats.totalRecipients) * 100).toFixed(1) : 0}%
                        </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-gray-600 text-xs mb-1">Bajas</p>
                        <p className="text-2xl font-bold text-gray-600">{stats.unsubscribedCount || 0}</p>
                        <p className="text-xs text-gray-500 mt-1">
                            {stats.totalRecipients > 0 ? (((stats.unsubscribedCount || 0) / stats.totalRecipients) * 100).toFixed(1) : 0}%
                        </p>
                    </div>
                </div>

                {/* Información adicional */}
                {stats.timestamps && (
                    <div className="pt-4 border-t border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">Información de Envío</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                            {stats.timestamps.firstSentAt && (
                                <p>Primer envío: {new Date(stats.timestamps.firstSentAt).toLocaleString()}</p>
                            )}
                            {stats.timestamps.lastSentAt && (
                                <p>Último envío: {new Date(stats.timestamps.lastSentAt).toLocaleString()}</p>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default CampaignProgressPage;

